import { type FirebaseOptions } from 'firebase/app'
import { z } from 'zod'

/**
 * Push notifications only exist to deliver AI deals. With deals off, the Firebase
 * env vars aren't required, the service worker isn't built (see
 * `buildServiceWorker` in nextjs-config-utils) and the client stays inert.
 */
const IS_AI_DEALS_ENABLED = process.env.NEXT_PUBLIC_AI_DEALS_ENABLED === 'true'

const firebaseConfigSchema = z.object({
  apiKey: z.string().min(1),
  authDomain: z.string().min(1),
  projectId: z.string().min(1),
  messagingSenderId: z.string().min(1),
  appId: z.string().min(1)
})

let cachedConfig: FirebaseOptions | null | undefined

/**
 * Parsed lazily so importing this module never throws during build/prerender.
 * Returns `null` when deals are disabled or the config is incomplete.
 * @remarks keep literal `process.env.NEXT_PUBLIC_*` accesses — they are inlined at build time
 */
export function getFirebaseConfig(): FirebaseOptions | null {
  if (cachedConfig !== undefined) {
    return cachedConfig
  }

  if (!IS_AI_DEALS_ENABLED) {
    cachedConfig = null
    return cachedConfig
  }

  const result = firebaseConfigSchema.safeParse({
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID
  })

  if (!result.success) {
    // Surface misconfiguration: deals are on but push can't work
    console.warn(
      'AI deals are enabled but the Firebase config is incomplete; falling back to polling.',
      result.error.issues.map((issue) => issue.path.join('.'))
    )
  }

  cachedConfig = result.success ? result.data : null
  return cachedConfig
}
